import { DbUser, LtiTools } from "../../../../interfaces";
import React from "react";

export type LtiToolCreationState = {
  form: {
    toolName: string;
    toolDescription: string;
    toolOidcEndpoint: string;
    toolLaunchUrl: string;
    toolRedirectUris: string;
    toolJwkMethod: string;
    toolPublicJwk: string;
  };
  data: {
    ltiTools: {
      value: LtiTools[];
      succeeded: boolean;
    };
  };
  currentUser: DbUser;
};

export type UpdateFormAction = {
  type: "updateForm";
  update: Partial<LtiToolCreationState["form"]>;
};

export type ReplaceStateAction = {
  type: "replaceState";
  state: LtiToolCreationState;
};

export type DataSuccessAction<T extends keyof LtiToolCreationState["data"]> = {
  type: "dataSuccess";
  key: T;
  value: LtiToolCreationState["data"][T]["value"];
};

export type Action =
  | UpdateFormAction
  | ReplaceStateAction
  | DataSuccessAction<keyof LtiToolCreationState["data"]>;

export const dataSuccess = <T extends keyof LtiToolCreationState["data"]>({
  key,
  value,
}: Omit<DataSuccessAction<T>, "type">): DataSuccessAction<T> => ({
  type: "dataSuccess",
  key,
  value,
});

export const initLtiToolCreationState = (
  currentUser: DbUser,
  data?: LtiToolCreationState["data"]
): LtiToolCreationState => {
  const state: LtiToolCreationState = {
    form: {
      toolName: "",
      toolDescription: "",
      toolOidcEndpoint: "",
      toolLaunchUrl: "",
      toolRedirectUris: "",
      toolJwkMethod: "",
      toolPublicJwk: "",
    },
    data: data ?? {
      ltiTools: {
        value: [],
        succeeded: false,
      },
    },
    currentUser,
  };
  return state;
};

function reducer(state: LtiToolCreationState, action: Action): LtiToolCreationState {
  switch (action.type) {
    case "replaceState":
      return action.state;
    case "updateForm": {
      return {
        ...state,
        form: {
          ...state.form,
          ...action.update,
        },
      };
    }
    case "dataSuccess": {
      return {
        ...state,
        data: {
          ...state.data,
          [action.key]: {
            value: action.value,
            succeeded: true,
          },
        },
      };
    }
  }
}

export function getError(state: LtiToolCreationState): string | undefined {
  // if (state.form.toolName.trim() === "") {
  //   return "Tool name is required.";
  // }
  // if (state.form.toolDescription.trim() === "") {
  //   return "Tool description is required.";
  // }
  if (state.form.toolOidcEndpoint.trim() === "") {
    return "Tool OIDC endpoint is required.";
  }
  if (state.form.toolLaunchUrl.trim() === "") {
    return "Tool launch URL is required.";
  }
  if (state.form.toolRedirectUris.trim() === "") {
    return "Tool redirect URIs is required.";
  }
  if (state.form.toolJwkMethod.trim() === "") {
    return "Tool JWK method is required.";
  }
  if (state.form.toolPublicJwk.trim() === "") {
    return "Tool public JWK is required.";
  }
}

export function useLtiToolCreationState(
  currentUser: DbUser,
  data?: LtiToolCreationState["data"]
): [LtiToolCreationState, React.Dispatch<Action>] {
  const [state, dispatch] = React.useReducer(
    reducer,
    initLtiToolCreationState(currentUser, data)
  );

  return [state, dispatch];
}
